import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { trackPurchase } from "@/lib/analytics";
import {
  Loader2, Lock, ArrowRight, CreditCard, FileText, CheckCircle2, Plus, Trash2, Percent,
} from "lucide-react";
import { COURSE_LIST } from "@/data/courses";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Pakete sind Festpreis pro Pflegedienst → nicht pro Teilnehmer:in wählbar
const SELECTABLE_COURSES = COURSE_LIST.filter((c) => !c.id.startsWith("paket-"));

const EMPTY_PARTICIPANT = { first_name: "", last_name: "", birthdate: "", email: "" };

export function parsePrice(priceStr) {
  return parseFloat(String(priceStr).replace(/[^\d,.]/g, "").replace(/\./g, "").replace(",", "."));
}

export function b2bDiscount(n) {
  if (n >= 8) return 0.2;
  if (n >= 5) return 0.15;
  if (n >= 3) return 0.1;
  return 0;
}

const fmtEur = (v) => v.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";

function Field({ id, label, children }) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} className="text-[12px] font-semibold text-sand-700 uppercase tracking-wider">{label}</Label>
      {children}
    </div>
  );
}

const inputCls = "h-11 rounded-xl bg-cream-50 border-cream-300 text-sand-800 placeholder:text-sand-400 focus-visible:ring-terra";

export default function B2BBookingForm({ course, onDone }) {
  const [contact, setContact] = useState({
    organisation: "", contact_first_name: "", contact_last_name: "",
    contact_email: "", contact_phone: "", street: "", zip_code: "", city: "",
  });
  const [participants, setParticipants] = useState(() => [{ ...EMPTY_PARTICIPANT, course_id: course.id }]);
  const [payMethod, setPayMethod] = useState("rechnung");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [promo, setPromo] = useState("");
  const [promoApplied, setPromoApplied] = useState(null);
  const [promoChecking, setPromoChecking] = useState(false);

  const applyPromo = async () => {
    if (!promo.trim()) return;
    setPromoChecking(true);
    try {
      const res = await axios.get(`${API}/discount/${encodeURIComponent(promo.trim())}`);
      setPromoApplied(res.data);
      toast.success(`Rabatt-Code eingelöst: −${res.data.pct} %`);
    } catch {
      setPromoApplied(null);
      toast.error("Ungültiger Rabatt-Code.");
    } finally {
      setPromoChecking(false);
    }
  };

  const setC = (key) => (e) => setContact((c) => ({ ...c, [key]: e.target.value }));
  const setP = (idx, key) => (e) =>
    setParticipants((ps) => ps.map((p, i) => (i === idx ? { ...p, [key]: e.target.value } : p)));

  const n = participants.length;
  const teamDiscount = b2bDiscount(n);
  const codeDiscount = promoApplied ? promoApplied.pct / 100 : 0;
  // Nicht kombinierbar – es gilt der höhere Rabatt (wie im Backend)
  const discount = Math.max(teamDiscount, codeDiscount);
  const discountIsCode = codeDiscount > teamDiscount;
  // Gemischte Kurse: Preis pro Teilnehmer:in nach gewähltem Kurs, Rabatt über die Gesamtzahl
  const groups = {};
  participants.forEach((p) => {
    const c = COURSE_LIST.find((x) => x.id === p.course_id);
    if (!c) return;
    if (!groups[c.id]) groups[c.id] = { title: c.title, unit: parsePrice(c.price), count: 0 };
    groups[c.id].count += 1;
  });
  const subtotal = Object.values(groups).reduce((s, g) => s + g.unit * g.count, 0);
  const total = subtotal * (1 - discount);
  const nextTier = n < 3 ? { need: 3 - n, pct: 10 } : n < 5 ? { need: 5 - n, pct: 15 } : n < 8 ? { need: 8 - n, pct: 20 } : null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/bookings/b2b`, {
        course_id: course.id,
        origin_url: window.location.origin,
        ...contact,
        participants,
        payment_method: payMethod,
        discount_code: promoApplied?.code || null,
      });
      if (payMethod === "online" && res.data?.url) {
        window.location.href = res.data.url;
        return;
      }
      setSuccess(res.data);
      trackPurchase(res.data?.amount_total || 0, res.data?.booking_id, "b2b");
      setSubmitting(false);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Anmeldung konnte nicht übermittelt werden – bitte prüfe die Angaben.");
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div data-testid="b2b-success" className="px-7 pb-8 pt-6 text-center">
        <CheckCircle2 className="h-12 w-12 text-sage-dark mx-auto mb-4" strokeWidth={1.4} />
        <h3 className="font-display text-2xl font-normal tracking-tight text-sand-800">Team-Anmeldung eingegangen!</h3>
        <p className="text-sand-600 text-[14.5px] leading-relaxed mt-2">
          {n} Teilnehmer:in{n > 1 ? "nen" : ""} angemeldet (
          {Object.values(groups).map((g) => `${g.count} × ${g.title}`).join(", ")})
          {success.discount_pct > 0 && <> – mit <strong className="text-sand-800">{success.discount_pct} % Teamrabatt</strong></>}.
          Die Rechnung über <strong className="text-sand-800">{fmtEur(success.amount_total)}</strong> geht
          per E-Mail an {contact.contact_email}.
        </p>
        <Button onClick={onDone} data-testid="b2b-success-close" className="mt-6 rounded-full bg-terra hover:bg-terra-dark text-white px-8">
          Alles klar
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="px-7 pb-7 pt-5 space-y-5" data-testid="b2b-form">
      {/* Einrichtung & Ansprechpartner */}
      <div>
        <div className="text-[11px] uppercase tracking-[0.24em] font-bold text-terra mb-3">1 · Einrichtung & Ansprechpartner</div>
        <div className="space-y-4">
          <Field id="b2b-org" label="Einrichtung / Firma *">
            <Input id="b2b-org" data-testid="b2b-organisation" className={inputCls} required minLength={2}
              placeholder="Pflegedienst Sonnenschein GmbH" value={contact.organisation} onChange={setC("organisation")} />
          </Field>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field id="b2b-cf" label="Vorname Ansprechpartner *">
              <Input id="b2b-cf" data-testid="b2b-contact-first" className={inputCls} required minLength={2}
                value={contact.contact_first_name} onChange={setC("contact_first_name")} />
            </Field>
            <Field id="b2b-cl" label="Nachname *">
              <Input id="b2b-cl" data-testid="b2b-contact-last" className={inputCls} required minLength={2}
                value={contact.contact_last_name} onChange={setC("contact_last_name")} />
            </Field>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field id="b2b-ce" label="E-Mail (Rechnung) *">
              <Input id="b2b-ce" data-testid="b2b-contact-email" className={inputCls} type="email" required
                value={contact.contact_email} onChange={setC("contact_email")} />
            </Field>
            <Field id="b2b-cp" label="Telefon *">
              <Input id="b2b-cp" data-testid="b2b-contact-phone" className={inputCls} type="tel" required minLength={5}
                value={contact.contact_phone} onChange={setC("contact_phone")} />
            </Field>
          </div>
          <Field id="b2b-street" label="Straße &amp; Hausnummer (Rechnungsadresse) *">
            <Input id="b2b-street" data-testid="b2b-street" className={inputCls} required minLength={3}
              value={contact.street} onChange={setC("street")} />
          </Field>
          <div className="grid grid-cols-[120px_1fr] gap-4">
            <Field id="b2b-zip" label="PLZ *">
              <Input id="b2b-zip" data-testid="b2b-zip" className={inputCls} required minLength={4} maxLength={10}
                inputMode="numeric" value={contact.zip_code} onChange={setC("zip_code")} />
            </Field>
            <Field id="b2b-city" label="Ort *">
              <Input id="b2b-city" data-testid="b2b-city" className={inputCls} required minLength={2}
                value={contact.city} onChange={setC("city")} />
            </Field>
          </div>
        </div>
      </div>

      {/* Teilnehmer */}
      <div>
        <div className="text-[11px] uppercase tracking-[0.24em] font-bold text-terra mb-3">
          2 · Teilnehmer:innen ({n})
        </div>
        <div className="space-y-3">
          {participants.map((p, i) => (
            <div key={i} className="rounded-xl border border-cream-300 bg-cream-50 p-4 space-y-3" data-testid={`b2b-participant-${i}`}>
              <div className="flex items-center justify-between">
                <span className="text-[12px] font-bold text-sand-700">Teilnehmer:in {i + 1}</span>
                {participants.length > 1 && (
                  <button type="button" data-testid={`b2b-remove-participant-${i}`}
                    onClick={() => setParticipants((ps) => ps.filter((_, idx) => idx !== i))}
                    className="inline-flex items-center gap-1 text-[11.5px] text-sand-500 hover:text-terra">
                    <Trash2 className="h-3 w-3" strokeWidth={1.8} /> Entfernen
                  </button>
                )}
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <Input data-testid={`b2b-p${i}-first`} className={inputCls} required minLength={2}
                  placeholder="Vorname *" value={p.first_name} onChange={setP(i, "first_name")} />
                <Input data-testid={`b2b-p${i}-last`} className={inputCls} required minLength={2}
                  placeholder="Nachname *" value={p.last_name} onChange={setP(i, "last_name")} />
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <Input data-testid={`b2b-p${i}-birthdate`} className={inputCls} type="date" required
                  max={new Date().toISOString().split("T")[0]} value={p.birthdate} onChange={setP(i, "birthdate")} />
                <Input data-testid={`b2b-p${i}-email`} className={inputCls} type="email" required
                  placeholder="E-Mail *" value={p.email} onChange={setP(i, "email")} />
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-sand-600 uppercase tracking-wider mb-1.5">Kurs *</label>
                <select
                  data-testid={`b2b-p${i}-course`}
                  required
                  value={p.course_id}
                  onChange={setP(i, "course_id")}
                  className="w-full h-11 rounded-xl bg-white border border-cream-300 text-sand-800 text-[13.5px] px-3 focus:outline-none focus:ring-2 focus:ring-terra"
                >
                  {SELECTABLE_COURSES.map((c) => (
                    <option key={c.id} value={c.id}>{c.title} · {c.price}</option>
                  ))}
                </select>
              </div>
            </div>
          ))}
          <button type="button" data-testid="b2b-add-participant"
            onClick={() => setParticipants((ps) => [...ps, { ...EMPTY_PARTICIPANT, course_id: course.id }])}
            className="w-full rounded-xl border border-dashed border-sand-400 hover:border-terra hover:text-terra text-sand-600 py-3 text-[13px] font-semibold inline-flex items-center justify-center gap-2 transition-colors">
            <Plus className="h-4 w-4" strokeWidth={2} /> Teilnehmer:in hinzufügen
          </button>
          <p className="text-[12px] text-sand-500 px-1">
            Tipp: Jede:r Teilnehmer:in kann einen <strong className="text-sand-700">anderen Kurs</strong> belegen – der Teamrabatt zählt über alle Kurse zusammen.
          </p>
        </div>
      </div>

      {/* Preis-Zusammenfassung mit Staffelrabatt */}
      <div className="rounded-xl bg-sand-800 text-cream-50 p-5" data-testid="b2b-price-summary">
        {Object.values(groups).map((g) => (
          <div key={g.title} className="flex justify-between text-[13.5px] mt-1 first:mt-0">
            <span>{g.count} × {g.title.length > 36 ? g.title.slice(0, 36) + "…" : g.title}</span>
            <span>{fmtEur(g.unit * g.count)}</span>
          </div>
        ))}
        {discount > 0 && (
          <div className="flex justify-between text-[13.5px] mt-1.5 text-peach-200" data-testid="b2b-discount-line">
            <span className="inline-flex items-center gap-1.5">
              <Percent className="h-3.5 w-3.5" strokeWidth={2} />
              {discountIsCode
                ? `Rabatt-Code ${promoApplied.code} (−${promoApplied.pct} %)`
                : `Teamrabatt (−${Math.round(discount * 100)} % · ${n} Teilnehmer gesamt)`}
            </span>
            <span>−{fmtEur(subtotal * discount)}</span>
          </div>
        )}
        <div className="flex justify-between font-semibold text-[16px] mt-3 pt-3 border-t border-white/15">
          <span>Gesamt</span>
          <span data-testid="b2b-total">{fmtEur(total)}</span>
        </div>
        {nextTier && (
          <p className="text-[12px] text-cream-200/80 mt-2.5">
            Noch {nextTier.need} Teilnehmer:in{nextTier.need > 1 ? "nen" : ""} bis −{nextTier.pct} % Teamrabatt.
          </p>
        )}
      </div>

      {/* Rabatt-Code */}
      <div className="space-y-1.5">
        <Label htmlFor="b2b-promo" className="text-[12px] font-semibold text-sand-700 uppercase tracking-wider">Rabatt-Code (optional)</Label>
        <div className="flex gap-2">
          <Input id="b2b-promo" data-testid="b2b-promo-input" className={inputCls}
            placeholder="z. B. HELDEN20" value={promo}
            onChange={(e) => { setPromo(e.target.value); if (promoApplied) setPromoApplied(null); }} />
          <Button type="button" data-testid="b2b-promo-apply" onClick={applyPromo}
            disabled={promoChecking || !promo.trim()}
            className="rounded-xl bg-sand-800 hover:bg-sand-900 text-cream-50 px-5 shrink-0">
            {promoChecking ? <Loader2 className="h-4 w-4 animate-spin" /> : "Einlösen"}
          </Button>
        </div>
        {promoApplied && (
          <p className="text-[12px] text-sage-dark font-semibold" data-testid="b2b-promo-applied">
            Code {promoApplied.code} aktiv (−{promoApplied.pct} %){!discountIsCode && " – euer Teamrabatt ist bereits höher und gilt weiterhin."}
          </p>
        )}
      </div>

      {/* Zahlungsart */}
      <div className="space-y-1.5">
        <Label className="text-[12px] font-semibold text-sand-700 uppercase tracking-wider">Zahlungsart *</Label>
        <div className="grid sm:grid-cols-2 gap-3">
          <button type="button" data-testid="b2b-pay-invoice" onClick={() => setPayMethod("rechnung")}
            className={`flex items-start gap-3 rounded-xl border p-4 text-left transition-colors ${payMethod === "rechnung" ? "border-terra bg-terra/5" : "border-cream-300 bg-cream-50 hover:border-sand-400"}`}>
            <FileText className={`h-5 w-5 mt-0.5 shrink-0 ${payMethod === "rechnung" ? "text-terra" : "text-sand-500"}`} strokeWidth={1.7} />
            <span>
              <span className="block text-[13.5px] font-semibold text-sand-800">Auf Rechnung</span>
              <span className="block text-[12px] text-sand-600 mt-0.5">Rechnung per E-Mail · Überweisung</span>
            </span>
          </button>
          <button type="button" data-testid="b2b-pay-online" onClick={() => setPayMethod("online")}
            className={`flex items-start gap-3 rounded-xl border p-4 text-left transition-colors ${payMethod === "online" ? "border-terra bg-terra/5" : "border-cream-300 bg-cream-50 hover:border-sand-400"}`}>
            <CreditCard className={`h-5 w-5 mt-0.5 shrink-0 ${payMethod === "online" ? "text-terra" : "text-sand-500"}`} strokeWidth={1.7} />
            <span>
              <span className="block text-[13.5px] font-semibold text-sand-800">Online bezahlen</span>
              <span className="block text-[12px] text-sand-600 mt-0.5">Karte oder Klarna</span>
            </span>
          </button>
        </div>
      </div>

      <Button type="submit" disabled={submitting} data-testid="b2b-submit"
        className="w-full rounded-full bg-terra hover:bg-terra-dark text-white py-3.5 text-base font-semibold group disabled:opacity-50">
        {submitting ? (
          <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> {payMethod === "rechnung" ? "Anmeldung wird übermittelt …" : "Weiterleitung zu Stripe …"}</>
        ) : (
          <>{payMethod === "rechnung" ? "Team zahlungspflichtig anmelden" : "Weiter zur Zahlung"} ({fmtEur(total)})
            <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" strokeWidth={1.8} /></>
        )}
      </Button>

      <p className="flex items-start gap-2 text-[11.5px] text-sand-500 leading-relaxed">
        <Lock className="h-3.5 w-3.5 mt-0.5 shrink-0" strokeWidth={1.6} />
        <span>
          Mit der Anmeldung akzeptieren Sie unsere{" "}
          <Link to="/agb" className="underline underline-offset-2 hover:text-sand-800">AGB</Link> und{" "}
          <Link to="/datenschutz" className="underline underline-offset-2 hover:text-sand-800">Datenschutzerklärung</Link>.
        </span>
      </p>
    </form>
  );
}
